import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Search, RefreshCw, Filter, Download, Settings, FileText, Mail, Send } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_OPTIONS = [
  { value: 'all', label: 'Alle Aktionen' },
  { value: 'customer.delete', label: 'Kunde gelöscht' },
  { value: 'customer.anonymize', label: 'Kunde anonymisiert' },
  { value: 'customer.purge_email', label: 'E-Mail freigegeben' },
  { value: 'customer.merge', label: 'Kunden zusammengeführt' },
  { value: 'payment.refund', label: 'Zahlung rückerstattet' },
  { value: 'subscription.cancel', label: 'Abo gekündigt' },
  { value: 'user.update_permissions', label: 'Berechtigungen geändert' },
  { value: 'package.delete', label: 'Paket gelöscht' },
  { value: 'invoice.cancel', label: 'Rechnung storniert' },
  { value: 'coupon.create', label: 'Coupon erstellt' },
  { value: 'audit.export_csv', label: 'CSV-Export' },
  { value: 'audit.dsgvo_pdf', label: 'DSGVO-PDF erstellt' },
  { value: 'audit.retention_change', label: 'Retention geändert' },
];

const ACTION_LABEL = {
  'customer.delete': { label: 'Kunde gelöscht', color: 'bg-red-500/20 text-red-400 border-red-500/40' },
  'customer.anonymize': { label: 'Anonymisiert', color: 'bg-orange-500/20 text-orange-400 border-orange-500/40' },
  'customer.purge_email': { label: 'E-Mail freigegeben', color: 'bg-blue-500/20 text-blue-400 border-blue-500/40' },
  'customer.merge': { label: 'Kunden gemerged', color: 'bg-purple-500/20 text-purple-400 border-purple-500/40' },
  'payment.refund': { label: 'Refund', color: 'bg-pink-500/20 text-pink-400 border-pink-500/40' },
  'subscription.cancel': { label: 'Abo gekündigt', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40' },
  'user.update_permissions': { label: 'Permissions', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/40' },
  'package.delete': { label: 'Paket gelöscht', color: 'bg-rose-500/20 text-rose-400 border-rose-500/40' },
  'invoice.cancel': { label: 'Rechnung storniert', color: 'bg-amber-500/20 text-amber-400 border-amber-500/40' },
  'coupon.create': { label: 'Coupon erstellt', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40' },
  'audit.export_csv': { label: 'CSV-Export', color: 'bg-slate-500/20 text-slate-400 border-slate-500/40' },
  'audit.dsgvo_pdf': { label: 'DSGVO-PDF', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40' },
  'audit.retention_change': { label: 'Retention', color: 'bg-slate-500/20 text-slate-400 border-slate-500/40' },
};

const PAGE_SIZE = 50;

export default function AuditLogPage() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [actionFilter, setActionFilter] = useState('all');
  const [actorEmailFilter, setActorEmailFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');

  const token = localStorage.getItem('token');

  // Retention-Setting (DSGVO-Cleanup-Tage)
  const [retentionDays, setRetentionDays] = useState(90);
  const [retentionDialogOpen, setRetentionDialogOpen] = useState(false);
  const [retentionInput, setRetentionInput] = useState('90');
  const [savingRetention, setSavingRetention] = useState(false);

  // Monatlicher DSGVO-Report (Auto-Versand)
  const [monthlyDialogOpen, setMonthlyDialogOpen] = useState(false);
  const [monthlySettings, setMonthlySettings] = useState({
    enabled: false, email: '', period_days: 30, last_sent_at: null, last_status: null,
  });
  const [monthlyEmailInput, setMonthlyEmailInput] = useState('');
  const [monthlyEnabledInput, setMonthlyEnabledInput] = useState(false);
  const [monthlyPeriodInput, setMonthlyPeriodInput] = useState(30);
  const [savingMonthly, setSavingMonthly] = useState(false);
  const [sendingMonthlyNow, setSendingMonthlyNow] = useState(false);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', PAGE_SIZE);
      params.set('skip', skip);
      if (actionFilter && actionFilter !== 'all') params.set('action', actionFilter);
      if (actorEmailFilter.trim()) params.set('actor_email', actorEmailFilter.trim());
      if (targetFilter.trim()) params.set('target', targetFilter.trim());

      const res = await axios.get(`${API}/customers/audit-log?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEntries(res.data?.entries || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      toast.error('Audit-Log konnte nicht geladen werden: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [skip, actionFilter, actorEmailFilter, targetFilter, token]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  // Retention beim Laden mitziehen
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/customers/audit-log/retention`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = res.data?.days || 90;
        setRetentionDays(d);
        setRetentionInput(String(d));
      } catch {
        // silent
      }
    })();
  }, [token]);

  // Monatliche DSGVO-Report-Settings beim Laden mitziehen
  const fetchMonthlySettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/customers/audit-log/dsgvo-monthly`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const s = res.data || {};
      setMonthlySettings({
        enabled: !!s.enabled,
        email: s.email || '',
        period_days: s.period_days || 30,
        last_sent_at: s.last_sent_at || null,
        last_status: s.last_status || null,
      });
      setMonthlyEmailInput(s.email || '');
      setMonthlyEnabledInput(!!s.enabled);
      setMonthlyPeriodInput(s.period_days || 30);
    } catch {
      // silent
    }
  }, [token]);

  useEffect(() => { fetchMonthlySettings(); }, [fetchMonthlySettings]);

  const saveRetention = async () => {
    const n = Number(retentionInput);
    if (!Number.isFinite(n) || n < 1 || n > 730) {
      toast.error('Bitte eine Zahl zwischen 1 und 730 Tagen eingeben');
      return;
    }
    setSavingRetention(true);
    try {
      await axios.put(
        `${API}/customers/audit-log/retention`,
        { days: n },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRetentionDays(n);
      setRetentionDialogOpen(false);
      toast.success(`Retention auf ${n} Tage gesetzt`);
      fetchLog();
    } catch (err) {
      toast.error('Speichern fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingRetention(false);
    }
  };

  const saveMonthlySettings = async () => {
    const email = (monthlyEmailInput || '').trim();
    if (monthlyEnabledInput && (!email.includes('@') || !email.includes('.'))) {
      toast.error('Bitte eine gültige E-Mail-Adresse angeben');
      return;
    }
    setSavingMonthly(true);
    try {
      await axios.put(
        `${API}/customers/audit-log/dsgvo-monthly`,
        { email, enabled: monthlyEnabledInput, period_days: Number(monthlyPeriodInput) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(monthlyEnabledInput
        ? `Monatlicher DSGVO-Report aktiviert für ${email}`
        : 'Monatlicher DSGVO-Report deaktiviert');
      setMonthlyDialogOpen(false);
      fetchMonthlySettings();
      fetchLog();
    } catch (err) {
      toast.error('Speichern fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingMonthly(false);
    }
  };

  const sendMonthlyNow = async () => {
    if (!monthlySettings.email) {
      toast.error('Erst eine E-Mail-Adresse speichern');
      return;
    }
    setSendingMonthlyNow(true);
    try {
      toast.info('DSGVO-Report wird verschickt…');
      const res = await axios.post(
        `${API}/customers/audit-log/dsgvo-monthly/send-now`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Report verschickt an ${res.data?.email}`);
      fetchMonthlySettings();
    } catch (err) {
      toast.error('Versand fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSendingMonthlyNow(false);
    }
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      params.set('days', String(retentionDays));
      if (actionFilter && actionFilter !== 'all') params.set('action', actionFilter);
      if (actorEmailFilter.trim()) params.set('actor_email', actorEmailFilter.trim());
      if (targetFilter.trim()) params.set('target', targetFilter.trim());

      const res = await axios.get(
        `${API}/customers/audit-log/export?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      const filename = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV-Export gestartet');
    } catch (err) {
      toast.error('Export fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
    }
  };

  const downloadDsgvoReport = async (days) => {
    try {
      toast.info('DSGVO-Report wird generiert…');
      const res = await axios.get(
        `${API}/customers/audit-log/dsgvo-report?days=${days}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const filename = `dsgvo-compliance-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`DSGVO-Report (${days} Tage) heruntergeladen`);
    } catch (err) {
      toast.error('Report fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
    }
  };

  const resetFilters = () => {
    setActionFilter('all');
    setActorEmailFilter('');
    setTargetFilter('');
    setSkip(0);
  };

  const formatTs = (ts) => {
    if (!ts) return '–';
    try {
      const d = new Date(ts);
      return d.toLocaleString('de-CH', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
    } catch {
      return ts;
    }
  };

  const renderMetadata = (entry) => {
    const md = entry.metadata || {};
    if (entry.action === 'customer.purge_email') {
      const parts = [];
      if (md.force) parts.push(<Badge key="f" variant="outline" className="border-yellow-500/50 text-yellow-400">force</Badge>);
      if (md.matched_user_id) parts.push(<span key="u" className="text-xs text-white/60">User vorhanden</span>);
      return <div className="flex gap-1 flex-wrap">{parts}</div>;
    }
    if (entry.action === 'customer.delete') {
      const emails = md.emails_purged || [];
      if (!emails.length) return <span className="text-white/40 text-xs">–</span>;
      return <span className="text-xs text-white/70">{emails.length} E-Mail{emails.length > 1 ? 's' : ''} gepurged</span>;
    }
    if (entry.action === 'customer.anonymize') {
      return md.original_email ? <span className="text-xs text-white/70">{md.original_email}</span> : <span className="text-white/40 text-xs">–</span>;
    }
    if (entry.action === 'customer.merge') {
      return (
        <span className="text-xs text-white/70">
          {md.source_name || '–'} → {md.target_name || '–'} ({md.transferred_records || 0} Datensätze)
        </span>
      );
    }
    if (entry.action === 'payment.refund') {
      return (
        <span className="text-xs text-white/70">
          CHF {(md.amount || 0).toFixed(2)} · {md.refund_method || 'keine'}
        </span>
      );
    }
    if (entry.action === 'subscription.cancel') {
      return (
        <span className="text-xs text-white/70">
          {md.subscription_name || '–'} ({md.customer_name || '–'}, {md.remaining_credits || 0} Restpunkte)
        </span>
      );
    }
    if (entry.action === 'user.update_permissions') {
      return (
        <span className="text-xs text-white/70">
          {md.target_email || '–'} ({md.target_role || '–'})
        </span>
      );
    }
    if (entry.action === 'package.delete') {
      return (
        <span className="text-xs text-white/70">
          {md.name || '–'} ({md.points || 0} Punkte)
        </span>
      );
    }
    if (entry.action === 'invoice.cancel') {
      return (
        <span className="text-xs text-white/70">
          {md.invoice_number || '–'} · CHF {(md.amount || 0).toFixed(2)}
          {md.points_reversed ? ` · ${md.points_reversed} Punkte zurück` : ''}
        </span>
      );
    }
    if (entry.action === 'coupon.create') {
      return (
        <span className="text-xs text-white/70">
          {md.code || '–'} · {md.discount_type === 'percentage' ? `${md.discount_value}%` : `CHF ${md.discount_value}`}
          {md.max_uses ? ` · max ${md.max_uses}×` : ''}
        </span>
      );
    }
    if (entry.action === 'audit.export_csv') {
      return (
        <span className="text-xs text-white/70">
          {md.row_count || 0} Zeilen · {md.days || 0} Tage
        </span>
      );
    }
    if (entry.action === 'audit.dsgvo_pdf') {
      const sizeKb = md.size_bytes ? Math.round(md.size_bytes / 1024) : 0;
      return (
        <span className="text-xs text-white/70">
          {md.days || 0} Tage · {sizeKb} KB
        </span>
      );
    }
    if (entry.action === 'audit.retention_change') {
      return (
        <span className="text-xs text-white/70">
          neu: {md.new_days || '–'} Tage
        </span>
      );
    }
    return <span className="text-white/40 text-xs">–</span>;
  };

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ShieldCheck size={32} className="text-yellow-400" />
            Audit-Log
          </h1>
          <p className="text-gray-400 mt-1 text-sm">
            Sicherheits-Protokoll aller administrativen Aktionen (DSGVO-relevant) ·
            Aufbewahrung: <span className="text-yellow-400 font-medium">{retentionDays} Tage</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            data-testid="audit-retention-btn"
            onClick={() => setRetentionDialogOpen(true)}
          >
            <Settings className="h-4 w-4 mr-2" /> Aufbewahrung
          </Button>
          <Button
            variant="outline"
            data-testid="audit-monthly-btn"
            onClick={() => setMonthlyDialogOpen(true)}
            className={monthlySettings.enabled ? 'border-emerald-500/40 hover:bg-emerald-500/10' : ''}
          >
            <Mail className={`h-4 w-4 mr-2 ${monthlySettings.enabled ? 'text-emerald-400' : ''}`} />
            Monats-Report
            {monthlySettings.enabled && (
              <Badge className="ml-2 bg-emerald-500/20 text-emerald-400 border-emerald-500/40 border text-[10px] px-1.5 py-0">AN</Badge>
            )}
          </Button>
          <Button
            variant="outline"
            data-testid="audit-export-csv-btn"
            onClick={exportCsv}
          >
            <Download className="h-4 w-4 mr-2" /> CSV-Export
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                data-testid="audit-dsgvo-pdf-btn"
                className="border-yellow-500/40 hover:bg-yellow-500/10"
              >
                <FileText className="h-4 w-4 mr-2 text-yellow-400" /> DSGVO-PDF
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-gray-900 border-white/10">
              <DropdownMenuLabel className="text-xs">Berichtszeitraum wählen</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => downloadDsgvoReport(7)} data-testid="audit-dsgvo-7d">Letzte 7 Tage</DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadDsgvoReport(30)} data-testid="audit-dsgvo-30d">Letzte 30 Tage</DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadDsgvoReport(90)} data-testid="audit-dsgvo-90d">Letzte 90 Tage</DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadDsgvoReport(180)} data-testid="audit-dsgvo-180d">Letzte 180 Tage</DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadDsgvoReport(365)} data-testid="audit-dsgvo-365d">Letztes Jahr</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" onClick={fetchLog} data-testid="audit-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Aktualisieren
          </Button>
        </div>
      </div>

      <Dialog open={retentionDialogOpen} onOpenChange={setRetentionDialogOpen}>
        <DialogContent className="bg-gray-900 border-white/10">
          <DialogHeader>
            <DialogTitle>Audit-Log Aufbewahrungszeit</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-white/70">
              Audit-Einträge werden nach dieser Anzahl Tagen automatisch
              entfernt (täglicher Cleanup-Job um 02:00 Uhr).
            </p>
            <div>
              <label className="text-xs text-white/60 mb-1 block">Tage (1–730)</label>
              <Input
                type="number"
                min={1}
                max={730}
                data-testid="audit-retention-days-input"
                value={retentionInput}
                onChange={(e) => setRetentionInput(e.target.value)}
              />
            </div>
            <div className="text-xs text-white/50">
              <strong>DSGVO-Hinweis:</strong> 90 Tage sind ein guter Kompromiss
              zwischen Forensik-Bedarf und Datensparsamkeit. Längere Zeiträume
              (180–365 Tage) sind bei Compliance-Audits sinnvoll.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRetentionDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              data-testid="audit-retention-save-btn"
              onClick={saveRetention}
              disabled={savingRetention}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              {savingRetention ? 'Speichert…' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={monthlyDialogOpen} onOpenChange={setMonthlyDialogOpen}>
        <DialogContent className="bg-gray-900 border-white/10 max-w-lg" data-testid="audit-monthly-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-emerald-400" />
              Monatlicher DSGVO-Report
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-white/70">
              Versende den DSGVO-Compliance-Report automatisch am{' '}
              <strong className="text-white">1. jedes Monats um 03:00 Uhr</strong>{' '}
              an die unten konfigurierte E-Mail-Adresse (z.B. Datenschutz-Beauftragte/r).
            </p>

            <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg p-3">
              <div>
                <div className="text-sm font-medium">Auto-Versand aktiv</div>
                <div className="text-xs text-white/50">Cron-Job läuft im Hintergrund</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="audit-monthly-enabled-toggle"
                  checked={monthlyEnabledInput}
                  onChange={(e) => setMonthlyEnabledInput(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
              </label>
            </div>

            <div>
              <label className="text-xs text-white/60 mb-1 block">Empfänger-E-Mail</label>
              <Input
                type="email"
                data-testid="audit-monthly-email-input"
                placeholder="datenschutz@strongfit.ch"
                value={monthlyEmailInput}
                onChange={(e) => setMonthlyEmailInput(e.target.value)}
              />
            </div>

            <div>
              <label className="text-xs text-white/60 mb-1 block">Berichtszeitraum (pro E-Mail)</label>
              <Select
                value={String(monthlyPeriodInput)}
                onValueChange={(v) => setMonthlyPeriodInput(Number(v))}
              >
                <SelectTrigger data-testid="audit-monthly-period-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Letzte 7 Tage</SelectItem>
                  <SelectItem value="30">Letzte 30 Tage (Standard)</SelectItem>
                  <SelectItem value="90">Letzte 90 Tage</SelectItem>
                  <SelectItem value="180">Letzte 180 Tage</SelectItem>
                  <SelectItem value="365">Letztes Jahr</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {monthlySettings.last_sent_at && (
              <div className="text-xs text-white/60 bg-white/5 rounded p-2 border border-white/10">
                <strong>Letzter Versand:</strong> {formatTs(monthlySettings.last_sent_at)}
                {' · '}
                <span className={monthlySettings.last_status === 'success' ? 'text-emerald-400' : 'text-red-400'}>
                  {monthlySettings.last_status === 'success' ? '✓ erfolgreich' : '✗ fehlgeschlagen'}
                </span>
              </div>
            )}

            <div className="text-xs text-white/50 border-t border-white/10 pt-3">
              <strong>Hinweis:</strong> SMTP muss in den Einstellungen konfiguriert sein.
              Der Report enthält alle administrativen Aktionen, verantwortlichen Admins
              sowie alle DSGVO-relevanten Lösch- und Anonymisierungs-Vorgänge.
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={sendMonthlyNow}
              disabled={sendingMonthlyNow || !monthlySettings.email}
              data-testid="audit-monthly-send-now-btn"
              className="border-emerald-500/40 hover:bg-emerald-500/10"
            >
              <Send className="h-4 w-4 mr-2 text-emerald-400" />
              {sendingMonthlyNow ? 'Sendet…' : 'Jetzt testen'}
            </Button>
            <div className="flex gap-2 sm:ml-auto">
              <Button variant="ghost" onClick={() => setMonthlyDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button
                data-testid="audit-monthly-save-btn"
                onClick={saveMonthlySettings}
                disabled={savingMonthly}
                className="bg-emerald-500 hover:bg-emerald-600 text-black"
              >
                {savingMonthly ? 'Speichert…' : 'Speichern'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Filter size={16} /> Filter
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-white/60 mb-1 block">Aktion</label>
              <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setSkip(0); }}>
                <SelectTrigger data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTION_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-white/60 mb-1 block">Admin-E-Mail</label>
              <Input
                data-testid="audit-actor-filter"
                placeholder="z.B. info@strongfit.ch"
                value={actorEmailFilter}
                onChange={(e) => setActorEmailFilter(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setSkip(0); fetchLog(); } }}
              />
            </div>
            <div>
              <label className="text-xs text-white/60 mb-1 block">Ziel (E-Mail oder ID)</label>
              <Input
                data-testid="audit-target-filter"
                placeholder="user@example.com oder Kunden-ID"
                value={targetFilter}
                onChange={(e) => setTargetFilter(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setSkip(0); fetchLog(); } }}
              />
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={() => { setSkip(0); fetchLog(); }} className="bg-yellow-500 hover:bg-yellow-600 text-black">
                <Search className="h-4 w-4 mr-1" /> Suchen
              </Button>
              <Button variant="ghost" onClick={resetFilters} data-testid="audit-reset-btn">
                Reset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>Einträge ({total.toLocaleString('de-CH')})</span>
            {total > PAGE_SIZE && (
              <div className="flex gap-2 items-center text-sm font-normal">
                <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}>
                  ←
                </Button>
                <span className="text-white/60">
                  {skip + 1}–{Math.min(skip + PAGE_SIZE, total)}
                </span>
                <Button variant="outline" size="sm" disabled={skip + PAGE_SIZE >= total} onClick={() => setSkip(skip + PAGE_SIZE)}>
                  →
                </Button>
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-white/60">Lädt…</div>
          ) : entries.length === 0 ? (
            <div className="py-10 text-center text-white/40">Keine Einträge gefunden</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10">
                    <TableHead className="text-white/60">Zeitpunkt</TableHead>
                    <TableHead className="text-white/60">Aktion</TableHead>
                    <TableHead className="text-white/60">Admin</TableHead>
                    <TableHead className="text-white/60">Ziel</TableHead>
                    <TableHead className="text-white/60">Details</TableHead>
                    <TableHead className="text-white/60">IP</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((e) => {
                    const meta = ACTION_LABEL[e.action] || { label: e.action, color: 'bg-white/10 text-white/70' };
                    return (
                      <TableRow key={e.id} className="border-white/10 hover:bg-white/5">
                        <TableCell className="text-xs font-mono whitespace-nowrap">{formatTs(e.ts)}</TableCell>
                        <TableCell>
                          <Badge className={`border ${meta.color}`}>{meta.label}</Badge>
                        </TableCell>
                        <TableCell className="text-xs">
                          <div className="font-medium">{e.actor_email || '–'}</div>
                          <div className="text-white/40">{e.actor_role || '–'}</div>
                        </TableCell>
                        <TableCell className="text-xs font-mono break-all max-w-xs">{e.target || '–'}</TableCell>
                        <TableCell>{renderMetadata(e)}</TableCell>
                        <TableCell className="text-xs text-white/50 font-mono">{e.ip || '–'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
